import {
  Alert,
  Button,
  Card,
  Flex,
  SimpleTable,
  Tooltip,
  Typography,
} from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const TooltipPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Tooltip</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Tooltip component in Djuno Design provides a simple way to add
            contextual information or hints to any element via hover or click
            interactions. It is highly customizable with various positioning
            options, themes, and interactive capabilities. Tooltips can display
            either simple text or custom content, providing users with
            additional information in a non-intrusive way.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Tooltip component offers the following customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Content
                </Text>
                : Displays either text or a custom React node as the tooltip
                content.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Placement
                </Text>
                : The tooltip can be positioned relative to the element in
                various directions, such as top, right, bottom, or left, with
                fine control over the alignment.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Theme
                </Text>
                : Choose between predefined themes like primary and error for
                different contextual cues.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Interactivity
                </Text>
                : Tooltips can be made clickable, allowing users to interact
                with the content, which is useful for actionable elements.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4">
            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                title="places"
                codeViewerProps={{
                  code: TooltipExample1,
                }}
              >
                <Flex items={{ default: "center" }} className="gap-8">
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip place="top" content="top content">
                      <Text>top</Text>
                    </Tooltip>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip place="right" content="right content">
                      <Text>right</Text>
                    </Tooltip>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip place="bottom" content="bottom content">
                      <Text>bottom</Text>
                    </Tooltip>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip place="left" content="left content">
                      <Text>left</Text>
                    </Tooltip>
                  </Flex>
                </Flex>
                <Alert
                  uiType="info"
                  className="mt-7"
                  showIcon={true}
                  message={
                    <Flex direction="row" items="center" className="gap-3">
                      <Text size="xs">
                        In each place, you can additionally use{" "}
                        <Text size="xs" strong>
                          start
                        </Text>{" "}
                        and{" "}
                        <Text size="xs" strong>
                          end
                        </Text>{" "}
                        options like{" "}
                        <Text size="xs" strong>
                          right-start
                        </Text>{" "}
                        or{" "}
                        <Text size="xs" strong>
                          bottom-end
                        </Text>
                      </Text>
                      <Tooltip place="top-start" content="top-start content">
                        <Button uiSize="small" uiType="light">
                          top-start
                        </Button>
                      </Tooltip>
                    </Flex>
                  }
                />
              </ExpandableCard>
            </Flex>
            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                title="themes"
                codeViewerProps={{
                  code: TooltipExample2,
                }}
              >
                <Flex items={{ default: "center" }} className="gap-8">
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip theme="primary" content="primary content">
                      <Text>
                        primary<Text size="sm">(default)</Text>
                      </Text>
                    </Tooltip>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip theme="error" content="error content">
                      <Text>error</Text>
                    </Tooltip>
                  </Flex>
                </Flex>
              </ExpandableCard>
              <ExpandableCard
                title="Interactivity"
                codeViewerProps={{
                  code: TooltipExample3,
                }}
              >
                <Flex items={{ default: "center" }} className="gap-8">
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip content="default content">
                      <Text>default</Text>
                    </Tooltip>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Tooltip content="ckickable content" clickable>
                      <Text>ckickable</Text>
                    </Tooltip>
                  </Flex>
                </Flex>
              </ExpandableCard>
            </Flex>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* content */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">content</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content to be displayed inside the tooltip. Can be a
                        simple string or a React node for complex content.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">undefined</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* clickable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">clickable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Makes the tooltip clickable, allowing user interaction
                        within the tooltip content.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* place */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">place</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the placement of the tooltip relative to the
                        element it is attached to.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L22"}
                        size="xs"
                        code
                      >
                        TooltipPlaces
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">top</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* theme */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">theme</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The theme or visual style of the tooltip. primary
                        displays default styles, while error highlights issues
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L35"}
                        size="xs"
                        code
                      >
                        TooltipThemeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">primary</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Content to be displayed inside the tooltip
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>

                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const TooltipExample1 = `<Flex items={{ default: "center" }} className="gap-8">
    <Flex direction="col" items="center" className="gap-1">
      <Tooltip place="top" content="top content">
        <Text>top</Text>
      </Tooltip>
    </Flex>
    <Flex direction="col" items="center" className="gap-1">
      <Tooltip place="right" content="right content">
        <Text>right</Text>
      </Tooltip>
    </Flex>
    <Flex direction="col" items="center" className="gap-1">
      <Tooltip place="bottom" content="bottom content">
        <Text>bottom</Text>
      </Tooltip>
    </Flex>
    <Flex direction="col" items="center" className="gap-1">
      <Tooltip place="left" content="left content">
        <Text>left</Text>
      </Tooltip>
    </Flex>
  </Flex>`;
const TooltipExample2 = `<Flex items={{ default: "center" }} className="gap-8">
  <Flex direction="col" items="center" className="gap-1">
    <Tooltip theme="primary" content="primary content">
      <Text>
        primary<Text size="sm">(default)</Text>
      </Text>
    </Tooltip>
  </Flex>
  <Flex direction="col" items="center" className="gap-1">
    <Tooltip theme="error" content="error content">
      <Text>error</Text>
    </Tooltip>
  </Flex>
</Flex>`;
const TooltipExample3 = `<Flex items={{ default: "center" }} className="gap-8">
  <Flex direction="col" items="center" className="gap-1">
    <Tooltip content="default content">
      <Text>default</Text>
    </Tooltip>
  </Flex>
  <Flex direction="col" items="center" className="gap-1">
    <Tooltip content="ckickable content" clickable>
      <Text>ckickable</Text>
    </Tooltip>
  </Flex>
</Flex>`;

export default TooltipPage;
